const normalizeSections = (sections) => {
    if (!Array.isArray(sections)) {
        return [];
    }

    return sections.map((section) => ({
        title: section?.title,
        heading: section?.heading,
        subtitleBold: section?.subtitleBold,
        subtitle: section?.subtitle,
    }));
};

const normalizeStateTexts = (stateTexts) => {
    if (!stateTexts) {
        return null;
    }

    if (Array.isArray(stateTexts)) {
        const hasKeyedEntries = stateTexts.some(
            (item) => item?.state,
        );

        if (!hasKeyedEntries) {
            return stateTexts;
        }

        return stateTexts.reduce((acc, item) => {
            const stateKey = item?.state;

            if (stateKey) {
                acc[stateKey] = item;
            }

            return acc;
        }, {});
    }

    return stateTexts;
};

const normalizeOrderDate = (orderDate) => {
    if (!orderDate) {
        return '';
    }

    const normalized = String(orderDate).replace(/\./g, '/').replace(/-/g, '/');
    const datePart = normalized.split(' ')[0];
    const parts = datePart.split('/');

    if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }

    return datePart;
};

export const normalizePurchasedBenefits = (purchasedBenefits) => {
    if (!Array.isArray(purchasedBenefits)) {
        return [];
    }

    return purchasedBenefits
        .map((benefit) => ({
            id: benefit?.id,
            name: benefit?.name ?? benefit?.title ?? '',
            slug: benefit?.slug,
            image: benefit?.logoUrl
                ?? benefit?.imagePath
                ?? null,
            orderDate: normalizeOrderDate(benefit?.orderDate),
        }))
        .filter((benefit) => benefit.name || benefit.image || benefit.orderDate);
};

const normalizeGuestSection = (data) => {
    const guestSection = data?.guestSection;
    const text = guestSection?.text
        ?? data?.must_be_logged_in_text
        ?? null;
    const buttonText = guestSection?.buttonText
        ?? data?.must_be_logged_in_button_text
        ?? null;

    if (!text && !buttonText) {
        return null;
    }

    return { text, buttonText };
};

const normalizeDirectusImage = (image) => {
    if (!image) {
        return null;
    }

    if (typeof image === 'string') {
        return image;
    }

    return image.imagePath;
};

const normalizeBenefitsGroups = (rawBenefitsGroups) => {
    if (!Array.isArray(rawBenefitsGroups)) {
        return [];
    }

    return rawBenefitsGroups.map((group) => ({
        id: group.id,
        title: group.title ?? group.name ?? '',
    }));
};

const normalizeHeaderTexts = (headerTexts) => {
    if (!headerTexts) {
        return [];
    }

    if (Array.isArray(headerTexts)) {
        return headerTexts;
    }

    return [headerTexts];
};

const extractRegulationsUrl = (headerTexts) => {
    const headerText = normalizeHeaderTexts(headerTexts)[0];

    if (!headerText) {
        return null;
    }

    return headerText.regulationsUrl
        ?? headerText.linkUrl;
};

export const parseHotYoterPageResponse = (data) => {
    if (!data) {
        return null;
    }

    const nestedBenefitsSection = data.benefitsSection ?? {};
    const nestedHowToGet = data.howToGet ?? {};

    const rawBenefitsGroups = nestedBenefitsSection.benefitsGroups ?? [];
    const benefitsGroups = normalizeBenefitsGroups(rawBenefitsGroups);

    const groupIds = data.groupIds ?? benefitsGroups.map((group) => group.id).filter((id) => id != null);

    const headerTexts = normalizeHeaderTexts(
        nestedHowToGet.headerTexts,
    );

    const howToGetSections = normalizeSections(
        nestedHowToGet.howToGetSections,
    );

    const relatedExpert = nestedBenefitsSection.relatedExpert;

    return {
        groupIds,
        title: data.title,
        slug: data.slug,
        internalTitle: data.internalTitle,
        isActive: data.isActive,
        stateTexts: normalizeStateTexts(data.stateTexts),
        howToGet: {
            headerTexts,
            desktopBackgroundImage: normalizeDirectusImage(
                nestedHowToGet?.desktopBackgroundImage,
            ),
            howToGetSections,
            desktopHowToGetDisclaimer: nestedHowToGet?.desktopHowToGetDisclaimer,
        },
        benefitsSection: {
            benefitsGroups,
            relatedExpert,
            searchPlaceholderText: nestedBenefitsSection.searchPlaceholderText ?? null,
        },
        guestSection: normalizeGuestSection(data),
        purchasedBenefits: normalizePurchasedBenefits(
            data.purchasedBenefits,
        ),
        currentState: data.currentState,
        regulationsUrl: extractRegulationsUrl(headerTexts),
    };
};
